import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Popup as PopupComponent, ScrollView, Button } from 'devextreme-react';
import { ToolbarItem } from 'devextreme-react/popup';
import { useScreenSize } from '../../../../../utils/media-query';
import { SystemFields } from '../../../../../types/types.config';
import { StudiesAddType } from '../../../../../types/types.orders';
import { CustomFormControl } from '../../../../utils/form-render/FormRender';
import ModalConfirmation from '../../../../utils/popup-confirmation/PopupConfirmation';
import newToast from '../../../../utils/new-toast/NewToast';
import { formatMessage } from 'devextreme/localization';

interface ModalAdditionalFieldsProps {
  visible: boolean;
  onHide: () => void;
  study: StudiesAddType | null;
  fields: SystemFields[];
  allStudies: StudiesAddType[];
  onSave: (studyId: number, fieldsData: Record<string, any>, applyToOthers?: { fieldName: string; value: any }[]) => void;
}

const ModalAdditionalFields: React.FC<ModalAdditionalFieldsProps> = ({
  visible,
  onHide,
  study,
  fields,
  allStudies,
  onSave,
}) => {
  const { isXSmall, isSmall } = useScreenSize();
  
  // Estado local para los campos adicionales
  const [localFields, setLocalFields] = useState<Record<string, any>>({});
  // Estado para el modal de confirmación de aplicar a otros
  const [showApplyToOthersModal, setShowApplyToOthersModal] = useState(false);
  // Campos que se modificaron y tienen otros estudios vacíos
  const [fieldsToApply, setFieldsToApply] = useState<{ fieldName: string; label: string; value: any }[]>([]);
  // Bandera para saber si se confirmó aplicar a otros
  const [pendingSaveWithApply, setPendingSaveWithApply] = useState(false);

  // Inicializar los campos locales cuando se abre el modal o cambia el estudio
  useEffect(() => {
    if (visible && study) {
      setLocalFields(study.AditionalFields || {});
    }
  }, [visible, study]);

  // Limpiar estado al cerrar
  useEffect(() => {
    if (!visible) {
      setLocalFields({});
      setFieldsToApply([]);
      setShowApplyToOthersModal(false);
      setPendingSaveWithApply(false);
    }
  }, [visible]);

  // Manejador para cambio de valor en un campo
  const handleFieldChange = useCallback((fieldEquivalencia: string, value: any, cast?: 'UPPER' | 'LOWER') => {
    let finalValue = value;
    
    // Aplicar cast si es necesario
    if (cast && typeof value === 'string') {
      finalValue = cast === 'UPPER' ? value.toUpperCase() : value.toLowerCase();
    }
    
    setLocalFields(prev => ({
      ...prev,
      [fieldEquivalencia]: finalValue,
    }));
  }, []);

  // Verificar si hay otros estudios con campos vacíos para los valores que se han llenado
  const checkForEmptyFieldsInOtherStudies = useCallback((): { fieldName: string; label: string; value: any }[] => {
    if (!study) return [];

    const dynamicFields = fields.filter(f => f.Dfc_Dinamico);
    const fieldsWithOthersEmpty: { fieldName: string; label: string; value: any }[] = [];

    // Verificar cada campo que tiene un valor en el estudio actual
    for (const field of dynamicFields) {
      const currentValue = localFields[field.Dfc_Equivalencia];
      const originalValue = study.AditionalFields?.[field.Dfc_Equivalencia];
      
      // Solo considerar si el campo tiene un valor y fue modificado (o es nuevo)
      if (currentValue !== undefined && currentValue !== null && currentValue !== '' && currentValue !== originalValue) {
        // Verificar si hay otros estudios con este campo vacío
        const otherStudiesWithEmptyField = allStudies.filter(s => {
          if (s.Es_Id === study.Es_Id) return false; // Excluir el estudio actual
          
          const otherValue = s.AditionalFields?.[field.Dfc_Equivalencia];
          return otherValue === undefined || otherValue === null || otherValue === '';
        });

        if (otherStudiesWithEmptyField.length > 0) {
          fieldsWithOthersEmpty.push({
            fieldName: field.Dfc_Equivalencia,
            label: field.Dfc_Label,
            value: currentValue,
          });
        }
      }
    }

    return fieldsWithOthersEmpty;
  }, [study, fields, localFields, allStudies]);

  // Manejador para guardar
  const handleSave = useCallback(() => {
    if (!study) return;

    // Verificar si hay campos que se pueden aplicar a otros estudios
    const fieldsWithOthersEmpty = checkForEmptyFieldsInOtherStudies();

    if (fieldsWithOthersEmpty.length > 0 && !pendingSaveWithApply) {
      // Mostrar modal de confirmación
      setFieldsToApply(fieldsWithOthersEmpty);
      setShowApplyToOthersModal(true);
    } else {
      // Guardar directamente
      onSave(study.Es_Id, localFields);
      newToast('SUCCESS', 'Campos adicionales guardados correctamente', 3);
      onHide();
    }
  }, [study, localFields, onSave, onHide, checkForEmptyFieldsInOtherStudies, pendingSaveWithApply]);

  // Confirmar aplicar a otros estudios
  const handleConfirmApplyToOthers = useCallback(() => {
    if (!study) return;
    
    // Convertir fieldsToApply al formato esperado por onSave
    const fieldsToApplyForSave = fieldsToApply.map(f => ({
      fieldName: f.fieldName,
      value: f.value,
    }));
    
    onSave(study.Es_Id, localFields, fieldsToApplyForSave);
    newToast('SUCCESS', `Campos adicionales guardados y aplicados a otros ${allStudies.filter(s => s.Es_Id !== study.Es_Id).length} estudios`, 3);
    setShowApplyToOthersModal(false);
    onHide();
  }, [study, localFields, fieldsToApply, onSave, onHide, allStudies]);

  // Cancelar aplicar a otros (solo guardar el estudio actual)
  const handleCancelApplyToOthers = useCallback(() => {
    if (!study) return;
    
    setPendingSaveWithApply(true);
    setShowApplyToOthersModal(false);
    // Guardar solo el estudio actual
    onSave(study.Es_Id, localFields);
    newToast('SUCCESS', 'Campos adicionales guardados correctamente', 3);
    onHide();
  }, [study, localFields, onSave, onHide]);

  // Renderizar los campos dinámicos
  const renderDynamicFields = useMemo(() => {
    const dynamicFields = fields.filter(f => f.Dfc_Dinamico);
    
    if (dynamicFields.length === 0) {
      return <p style={{ textAlign: 'center', color: '#888' }}>No hay campos adicionales configurados.</p>;
    }

    return dynamicFields.map(field => (
      <div key={field.Dfc_Id} style={{ marginBottom: '15px' }}>
        <CustomFormControl
          data={field}
          value={localFields[field.Dfc_Equivalencia] ?? null}
          onValueChange={(value) => handleFieldChange(field.Dfc_Equivalencia, value, field.Dfc_Cast)}
          isEditing={true}
        />
      </div>
    ));
  }, [fields, localFields, handleFieldChange]);

  // Mensaje para el modal de confirmación
  const confirmationMessage = useMemo(() => {
    if (fieldsToApply.length === 0) return '';
    
    const fieldLabels = fieldsToApply.map(f => f.label).join(', ');
    const otherStudiesCount = allStudies.filter(s => s.Es_Id !== study?.Es_Id).length;
    
    return `Desea ingrear los valores de (${fieldLabels}), a los ${otherStudiesCount} estudios pendientes?`;
  }, [fieldsToApply, allStudies, study]);

  if (!study) return null;

  return (
    <>
      <PopupComponent
        visible={visible}
        onHiding={onHide}
        dragEnabled={false}
        hideOnOutsideClick={false}
        showCloseButton={true}
        showTitle={true}
        title={formatMessage('OrdModalOrdEstRpm')}
        width={isXSmall || isSmall ? '100%' : 500}
        height='auto'
        maxHeight='80vh'
        fullScreen={isXSmall}
      >
        <ScrollView height='100%' showScrollbar='onScroll'>
          <div style={{ padding: '20px' }}>
            {/* Información del estudio */}
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Estudio:</div>
              <div>{study.Es_Codigo} - {study.Es_Nombre}</div>
            </div>

            {/* Campos dinámicos */}
            <div>
              {renderDynamicFields}
            </div>
          </div>
        </ScrollView>
        
        <ToolbarItem toolbar='bottom' location='after'>
          <div className='form-popup-buttons-container'>
            <Button 
              text='Cancelar' 
              stylingMode='contained' 
              onClick={onHide} 
            />
            <Button 
              text='Guardar' 
              stylingMode='contained' 
              type='default' 
              onClick={handleSave} 
            />
          </div>
        </ToolbarItem>
      </PopupComponent>

      {/* Modal de confirmación para aplicar a otros estudios */}
      <ModalConfirmation
        visible={showApplyToOthersModal}
        buttons='No/Yes'
        message={confirmationMessage}
        onConfirm={handleConfirmApplyToOthers}
        onCancel={handleCancelApplyToOthers}
      />
    </>
  );
};

export default ModalAdditionalFields;
